import type { ClefName } from 'shared/definitions/musicTheory/clefs';
import type { CheckedAnswer } from 'shared/definitions/checkpoint-definitions';
import SkillCalculator from '../uTheory.Skills/skillCalculator';
import utils from '../utils';
import KeySignature from '../uTheory.Notation/keySignature';
import Music from '../uTheory.Notation/music';
import Note from '../uTheory.Notation/note';
import type { Clef, NoteHolder } from '../notation';
import { Editable } from '../notation';

const restrictiveEditableSettings = {
  rhythm: false,
  accidental: false,
  pitch: false,
  deleteable: false,
  selectable: false,
  maxNotes: 0,
} as const;

/**
 * Question holder for NoteReadingPiano interactive
 */
export default class NoteReadingQuestion {
  clef!: Clef;

  /** just [ questionNumber ] in this case */
  text: any[];

  music!: Music;

  /** including only those in noteIndexes */
  noteHolders: NoteHolder[];

  editables: Editable[];

  /** 0 - 1 */
  percent: number;

  /** student's guesses in note names */
  guesses: Note[];

  /** an array of whether each question is right or not */
  isRight: boolean[];

  questionPart: number;

  private averager: ReturnType<typeof utils.averageFactory>;

  private skillsArr: (string | number)[][];

  lowestNote!: Note;

  highestNote!: Note;

  /**
   * Question object for noteReadingPiano interactive
   */
  constructor(
    question: string | { musicString: string; noteIndexes: number[] },
    defaultClef: ClefName,
    questionNumber: number
  ) {
    this.noteHolders = [];
    this.skillsArr = [];
    if (utils.isString(question)) this.processStringQuestion(question, defaultClef);
    else this.processObjectQuestion(question);
    this.questionPart = 0;
    this.averager = utils.averageFactory();
    this.text = [questionNumber + 1];
    this.percent = 0;
    this.guesses = [];
    this.isRight = [];
    this.editables = this.noteHolders.map((nh) => {
      const editable = new Editable(nh, restrictiveEditableSettings);
      nh.editable = editable;
      return editable;
    });
    this.noteHolders[0]!.editable!.selected = true;
  }

  /** Returns skills for current question part only. */
  get skills(): (string | number)[] {
    return this.skillsArr[this.questionPart - 1]!;
  }

  get allSkills(): (string | number)[] {
    return utils.unique(
      this.skillsArr.reduce(
        (allSkills: (string | number)[], theseSkills: (string | number)[]): (string | number)[] =>
          allSkills.concat(theseSkills),
        []
      )
    );
    // return utils.unique(Array.prototype.concat.call(...this.skillsArr));
  }

  get isOver(): boolean {
    return this.questionPart >= this.noteHolders.length;
  }

  /*
   * @typedef {object} CheckedAnswer
   * @property {boolean} correct true, false, or undefined if to be warned.
   * @property {string} warning text to warn user
   * @property {boolean} newQuestion if it's time for a new question
   * @property {number} midiNumber the correct midi number
   */

  /**
   * Check if an answer is correct, indicates whether it's time for a new question.
   * @param testMode - are we in test mode? (i.e., one try only?)
   */
  check(
    midiNumber: number,
    { testMode = false, ignoreOctave = false }: { testMode: boolean; ignoreOctave: boolean }
  ): CheckedAnswer & { midiNumber: number } {
    const rightMidi = this.noteHolders[this.questionPart]!.notes[0]!.getMidiNumber();
    const correctIgnoreOctave = rightMidi % 12 === midiNumber % 12;
    const correctExactOctave = rightMidi === midiNumber;
    let correct: boolean | undefined;
    if (!correctIgnoreOctave) {
      correct = false;
    } else if (ignoreOctave || correctExactOctave) {
      correct = true;
    }

    let warning: string;
    if (correct !== undefined) {
      warning = '';
    } else if (rightMidi > midiNumber) {
      warning = 'Octave too low';
    } else {
      warning = 'Octave too high';
    }

    if (correct || testMode) this.questionPart++;
    const newQuestion = this.questionPart >= this.noteHolders.length;

    if (correct !== undefined) {
      this.percent = this.averager(correct ? 1 : 0);
    }

    if (testMode && !warning) {
      this.guesses.push(Note.fromMidi(midiNumber));
      this.isRight.push(!!correct);
    }

    if (!newQuestion && (correct || testMode)) this.colorNextNote();

    return { correct, warning, newQuestion, midiNumber: rightMidi };
  }

  colorNextNote(): void {
    this.noteHolders.forEach((nh) => {
      nh.editable!.selected = false;
    });
    this.noteHolders[this.questionPart]!.editable!.selected = true;
    //  = highlightColor;
    if (this.noteHolders[this.questionPart - 1]) {
      this.noteHolders[this.questionPart - 1]!.color = undefined;
    }
  }

  processStringQuestion(question: string, defaultClef: ClefName): void {
    // If there is a key sig, set that:
    const clefName =
      ['treble', 'soprano', 'mezzo-soprano', 'alto', 'tenor', 'baritone', 'bass'].find(
        (clefString) => question.includes(clefString)
      ) || defaultClef;
    question = question.replace(clefName, '').trim();

    let key = 'C';

    if (question.search(':') !== -1) {
      const temp = question.split(':');
      key = temp[0] as string;
      question = temp[1] as string;
    }

    const notesString = question
      .split('/')
      .map((q) => `${q.trim()}/q`)
      .join(' ');
    const musicString = `key:${key}
       clef:${clefName} ${notesString}`;
    const music = Music.fromString(musicString);
    this.music = music;
    // Don't set time signature -- we want it invisible.
    // music.setTimeSignature('4/4');
    this.processMusic(music);
  }

  processObjectQuestion({
    musicString,
    noteIndexes,
  }: {
    musicString: string;
    noteIndexes?: number[];
  }): void {
    this.music = Music.fromString(musicString);
    this.processMusic(this.music, noteIndexes);
  }

  processMusic(music: Music, noteIndexes?: number[]): void {
    if (music.voices.length === 0 || music.staves.length === 0) {
      throw new Error('Music object is missing voices or staves.');
    }
    const noteHolders = music.voices[0]!.getPointers()
      .map((p) => p.noteHolder)
      .filter((nh: NoteHolder, i) => (noteIndexes ? noteIndexes.includes(i) : !nh.rhythm!.isTied))
      .filter((nh: NoteHolder) => !nh.isEmpty());
    const lowestNote = noteHolders
      .map((nh: NoteHolder): Note => nh.notes[0]!)
      .reduce((prior: Note, current: Note): Note => (prior.isLessThan(current) ? prior : current));
    const highestNote = noteHolders
      .map((nh: NoteHolder): Note => nh.notes[0]!)
      .reduce(
        (prior: Note, current: Note): Note => (prior.isGreaterThan(current) ? prior : current)
      );
    const clef = music.staves[0]!.clef;
    const key = music.keySignatures[0]!;

    const skills = noteHolders.map((nh: NoteHolder): (string | number)[] =>
      this.calculateSkills(nh.notes[0]!, key, clef, noteHolders)
    );

    this.noteHolders = noteHolders;
    this.lowestNote = lowestNote as Note;
    this.highestNote = highestNote as Note;
    this.clef = clef;
    this.skillsArr = skills;
  }

  /**
   * Calculate skills for a question part.
   */
  // eslint-disable-next-line class-methods-use-this
  calculateSkills(
    note: Note,
    key: KeySignature,
    clef: Clef,
    noteHolders: NoteHolder[]
  ): (string | number)[] {
    const noteIndex = noteHolders.findIndex((nh) => nh.notes[0] === note);
    const natural = !!noteHolders
      .map((nh: NoteHolder): Note => nh.notes[0]!)
      .filter(
        (n: Note, i: number): boolean =>
          n.accidental === '' &&
          n !== note &&
          i < noteIndex &&
          n.letter === note.letter &&
          n.accidental !== note.accidental
      ).length;

    const skillCalculator = new SkillCalculator();
    const skills: (string | number)[] = [
      skillCalculator.getClefSkill(note, clef),
      // Descreasing the student's piano skill causes confusion
      // since the piano skills come earlier in training, and clef
      // later, so, later work can hurt an earlier grade.
      // So: we've split them.
      // skillCalculator.getPianoSkill(note),
    ];

    if (natural) {
      skills.push('naturals');
    }

    key = KeySignature.stringToKey(key);
    if (key.equals && !key.equals('C Major')) {
      skills.push('note_in_key');
    }
    return skills;
  }
}
